import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Receipt, TrendingUp, MapPin, ShoppingBag, Loader2, DollarSign, CheckCircle, Clock, CreditCard, Calendar } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const fmt = (n) => (n || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const tipoLabel = { enganche: 'Pagada con enganche', pago_unico: 'Pago único', pagos: 'En pagos' };

export default function MisComisiones() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const authRes = await fetch(`${BACKEND_URL}/api/auth/me`, { credentials: 'include' });
        if (!authRes.ok) { navigate('/login'); return; }
        const res = await fetch(`${BACKEND_URL}/api/comisionista/mis-comisiones`, { credentials: 'include' });
        if (res.ok) setData(await res.json());
        else toast.error('Error al cargar comisiones');
      } catch { toast.error('Error de conexión'); }
      finally { setLoading(false); }
    };
    load();
  }, [navigate]);

  if (loading || !data) {
    return <div className="min-h-screen bg-zinc-950 flex items-center justify-center"><Loader2 className="w-8 h-8 text-[#C6A54A] animate-spin" /></div>;
  }

  return (
    <div data-testid="mis-comisiones-page" className="p-4 sm:p-6 space-y-6 max-w-[1000px] mx-auto">
      <div>
        <h1 className="text-2xl font-bold text-white flex items-center gap-3">
          <Receipt className="w-6 h-6 text-[#C6A54A]" />
          Mis Comisiones
        </h1>
        <p className="text-sm text-zinc-500 mt-1">Resumen y detalle de tu actividad comercial</p>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-zinc-900 rounded-2xl p-5 border border-zinc-800">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 bg-[#C6A54A]/20 rounded-lg flex items-center justify-center">
              <DollarSign className="w-4 h-4 text-[#C6A54A]" />
            </div>
            <span className="text-xs text-zinc-500 uppercase tracking-wider">Total Comisiones</span>
          </div>
          <p data-testid="total-comision" className="text-3xl font-bold text-[#C6A54A]">${fmt(data.total_comision)}</p>
        </div>
        <div className="bg-zinc-900 rounded-2xl p-5 border border-zinc-800">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 bg-emerald-500/20 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-4 h-4 text-emerald-400" />
            </div>
            <span className="text-xs text-zinc-500 uppercase tracking-wider">Pagadas</span>
          </div>
          <p data-testid="total-pagada" className="text-3xl font-bold text-emerald-400">${fmt(data.total_pagada)}</p>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div className="bg-zinc-900 rounded-xl p-4 border border-zinc-800 text-center">
          <div className="w-8 h-8 bg-amber-500/20 rounded-lg flex items-center justify-center mx-auto mb-2">
            <Clock className="w-4 h-4 text-amber-400" />
          </div>
          <p className="text-xs text-zinc-500 mb-1">Pendiente</p>
          <p data-testid="total-pendiente" className="text-xl font-bold text-amber-400">${fmt(data.total_pendiente)}</p>
        </div>
        <div className="bg-zinc-900 rounded-xl p-4 border border-zinc-800 text-center">
          <div className="w-8 h-8 bg-blue-500/20 rounded-lg flex items-center justify-center mx-auto mb-2">
            <ShoppingBag className="w-4 h-4 text-blue-400" />
          </div>
          <p className="text-xs text-zinc-500 mb-1">Ventas</p>
          <p data-testid="total-ventas" className="text-xl font-bold text-blue-400">{data.total_ventas}</p>
        </div>
        <div className="bg-zinc-900 rounded-xl p-4 border border-zinc-800 text-center">
          <div className="w-8 h-8 bg-[#C6A54A]/20 rounded-lg flex items-center justify-center mx-auto mb-2">
            <MapPin className="w-4 h-4 text-[#C6A54A]" />
          </div>
          <p className="text-xs text-zinc-500 mb-1">Apartados</p>
          <p data-testid="apartados-activos" className="text-xl font-bold text-[#C6A54A]">{data.apartados_activos}</p>
        </div>
      </div>

      {/* Detalle de comisiones */}
      {data.comisiones?.length > 0 ? (
        <div className="space-y-3">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-[#C6A54A]" /> Detalle de Comisiones
          </h2>
          {data.comisiones.map(c => (
            <div key={c.comision_id} data-testid={`comision-${c.comision_id}`}
              className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 hover:border-zinc-700 transition-colors">
              {/* Header */}
              <div className="flex items-start justify-between gap-3 mb-3">
                <div>
                  <p className="text-white font-semibold">
                    Lote #{c.lote_numero} <span className="text-xs text-zinc-500">Mz {c.lote_manzana}</span>
                  </p>
                  <p className="text-xs text-zinc-400 mt-0.5">
                    {c.fraccionamiento_nombre} · Cliente: {c.cliente_nombre || '-'}
                  </p>
                </div>
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium shrink-0 ${
                  c.estado === 'pagada' ? 'bg-emerald-500/20 text-emerald-400' :
                  c.estado === 'parcial' ? 'bg-amber-500/20 text-amber-400' :
                  'bg-zinc-700 text-zinc-300'
                }`}>{c.estado === 'pagada' ? 'Pagada' : c.estado === 'parcial' ? 'Parcial' : 'Pendiente'}</span>
              </div>

              {/* Amounts grid */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs mb-3">
                <div>
                  <p className="text-zinc-500">Precio Venta</p>
                  <p className="text-white font-medium">${fmt(c.precio_venta)}</p>
                </div>
                <div>
                  <p className="text-zinc-500">% Aplicado</p>
                  <p className="text-[#C6A54A] font-medium">{c.porcentaje_aplicado}%</p>
                </div>
                <div>
                  <p className="text-zinc-500">Comision</p>
                  <p className="text-white font-semibold">${fmt(c.monto_total)}</p>
                </div>
                <div>
                  <p className="text-zinc-500">Pendiente</p>
                  <p className={`font-semibold ${c.saldo_pendiente > 0 ? 'text-amber-400' : 'text-emerald-400'}`}>
                    ${fmt(c.saldo_pendiente)}
                  </p>
                </div>
              </div>

              {/* Liquidation type badge */}
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs text-zinc-500">Liquidación:</span>
                <span className="px-2 py-0.5 rounded-full text-xs bg-zinc-800 text-zinc-300">
                  {tipoLabel[c.tipo_liquidacion] || c.tipo_liquidacion || 'Pago único'}
                  {c.tipo_liquidacion === 'pagos' && c.numero_pagos ? ` (${c.numero_pagos})` : ''}
                </span>
              </div>

              {/* Scheduled payments */}
              {c.pagos_programados?.length > 0 && (
                <div className="border-t border-zinc-800 pt-3 mt-2">
                  <p className="text-xs text-zinc-500 mb-2 flex items-center gap-1">
                    <Calendar className="w-3 h-3" /> Plan de pagos
                  </p>
                  <div className="space-y-1.5">
                    {c.pagos_programados.map(pp => (
                      <div key={pp.pago_programado_id} className="flex items-center justify-between text-xs p-2 rounded-lg bg-zinc-800/50">
                        <div className="flex items-center gap-2">
                          <span className={`w-2 h-2 rounded-full ${pp.estado === 'pagado' ? 'bg-emerald-400' : 'bg-zinc-600'}`} />
                          <span className="text-zinc-400">Pago {pp.numero_pago}</span>
                          <span className="text-zinc-600">{new Date(pp.fecha_programada).toLocaleDateString('es-MX')}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`font-medium ${pp.estado === 'pagado' ? 'text-emerald-400' : 'text-white'}`}>
                            ${fmt(pp.monto)}
                          </span>
                          <span className={`px-1.5 py-0.5 rounded text-[10px] ${pp.estado === 'pagado' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-zinc-700 text-zinc-400'}`}>
                            {pp.estado === 'pagado' ? 'Pagado' : 'Pendiente'}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Actual payments made */}
              {c.pagos?.length > 0 && (
                <div className="border-t border-zinc-800 pt-2 mt-2">
                  <p className="text-xs text-zinc-500 mb-1">Pagos realizados:</p>
                  {c.pagos.map((p, i) => (
                    <div key={i} className="flex items-center justify-between text-xs py-1">
                      <span className="text-zinc-400">
                        {new Date(p.fecha).toLocaleDateString('es-MX')} · {p.metodo}
                        {p.referencia ? ` · ${p.referencia}` : ''}
                      </span>
                      <span className="text-emerald-400 font-medium">${fmt(p.monto)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-8 text-center">
          <TrendingUp className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
          <h3 className="text-white font-medium mb-1">Sin comisiones aún</h3>
          <p className="text-sm text-zinc-400">Aparta lotes y cierra tratos para generar comisiones.</p>
        </div>
      )}
    </div>
  );
}
